/**
 * مسیریابی، مشتق از حالت اجرا.
 *
 * ⚠️ **مسیرهای استودیو در حالت `assistant` ثبت نمی‌شوند، نه اینکه پنهان
 * شوند.** پس تایپ کردن مستقیم آدرس هم به آن‌ها نمی‌رسد. این لایه‌ی دوم است؛
 * لایه‌ی اول و واقعی، گارد `ensure_studio` سمت سرور است. پنهان‌کردن سمت کلاینت
 * هیچ‌گاه به‌عنوان کنترل دسترسی حساب نمی‌شود، چون bundle قابل دستکاری است.
 */

import { Link2Off, Loader2, Sparkles } from 'lucide-react'
import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router'

import { Banner } from './components/ui/primitives'
import { MetaProvider, useMetaQuery } from './lib/meta-context'

import { AppShell } from './components/layout/AppShell'
import { ArchivePage } from './features/archive/ArchivePage'
import { ConsultationHubPage } from './features/consultation/ConsultationHubPage'

/*
 * صفحات استودیو تنبل بارگذاری می‌شوند.
 *
 * دلیل اصلی اندازه‌ی bundle نیست، بلکه این است که در نسخه‌ی مشتری این کد
 * **هرگز دانلود نمی‌شود**. مشتری فقط مشاوره و آرشیو را می‌گیرد و کد مدیریت
 * دانش و انتشار حتی روی سیم هم نمی‌رود.
 */
const OrganizationsListPage = lazy(async () => ({
  default: (await import('./features/studio/organizations/OrganizationsListPage'))
    .OrganizationsListPage,
}))
const OrganizationDetailPage = lazy(async () => ({
  default: (await import('./features/studio/organizations/OrganizationDetailPage'))
    .OrganizationDetailPage,
}))
const AssistantsListPage = lazy(async () => ({
  default: (await import('./features/studio/assistants/AssistantsListPage')).AssistantsListPage,
}))
const AssistantDetailPage = lazy(async () => ({
  default: (await import('./features/studio/assistants/AssistantDetailPage')).AssistantDetailPage,
}))
const DossiersListPage = lazy(async () => ({
  default: (await import('./features/studio/dossiers/DossiersListPage')).DossiersListPage,
}))
const DossierWizardPage = lazy(async () => ({
  default: (await import('./features/studio/dossiers/DossierWizardPage')).DossierWizardPage,
}))
const BulkUploadPage = lazy(async () => ({
  default: (await import('./features/studio/dossiers/BulkUploadPage')).BulkUploadPage,
}))
const PrinciplesReviewPage = lazy(async () => ({
  default: (await import('./features/studio/dossiers/PrinciplesReviewPage'))
    .PrinciplesReviewPage,
}))
const ProcessingPage = lazy(async () => ({
  default: (await import('./features/studio/processing/ProcessingPage')).ProcessingPage,
}))
const PlaygroundPage = lazy(async () => ({
  default: (await import('./features/studio/PlaygroundPage')).PlaygroundPage,
}))
const PublishPage = lazy(async () => ({
  default: (await import('./features/studio/PublishPage')).PublishPage,
}))
const SettingsPage = lazy(async () => ({
  default: (await import('./features/studio/SettingsPage')).SettingsPage,
}))
const StudioArchivePage = lazy(async () => ({
  default: (await import('./features/studio/StudioArchivePage')).StudioArchivePage,
}))

/**
 * صفحه‌ی بوت. تا رسیدن `meta` هیچ چیز دیگری رندر نمی‌شود.
 *
 * نشان برند با نبض آرام می‌آید، نه یک spinner تنها: نخستین لحظه‌ی محصول است و
 * باید عمدی دیده شود، نه مثل صفحه‌ی نیمه‌بارگذاری‌شده.
 */
const BootScreen = () => (
  <div className="flex min-h-dvh flex-col items-center justify-center gap-5" role="status">
    <span className="relative grid size-14 place-items-center rounded-lg bg-linear-100 from-accent to-accent-two text-white shadow-glow ring-1 ring-inset ring-white/20">
      <Sparkles className="size-7" aria-hidden />
      <span
        className="animate-breathe absolute -inset-3 rounded-xl border border-accent/25"
        aria-hidden
      />
    </span>
    <p className="text-sm text-ink-muted">در حال آماده‌سازی دستیار…</p>
  </div>
)

/** نشانی عمومی‌ای که به دستیار زنده‌ای وصل نیست. */
const UnknownAssistant = () => (
  <div className="flex min-h-dvh items-center justify-center p-6">
    <div className="animate-rise w-full max-w-md text-center">
      <span
        className="mb-5 inline-grid size-12 place-items-center rounded-md bg-surface-sunken text-ink-faint ring-1 ring-line"
        aria-hidden
      >
        <Link2Off className="size-6" />
      </span>
      <h1 className="text-xl font-bold tracking-tight text-ink">این نشانی فعال نیست</h1>
      <p className="mt-2.5 text-sm leading-relaxed text-ink-muted">
        دستیاری روی این آدرس منتشر نشده است، یا از دسترس خارج شده. لینک تازه را از سازمان
        خود بگیرید.
      </p>
    </div>
  </div>
)

const BootError = ({ message }: { message: string }) => (
  <div className="flex min-h-dvh items-center justify-center p-6">
    <div className="w-full max-w-lg space-y-3">
      <Banner tone="danger" title="سرویس در دسترس نیست">
        {message}
      </Banner>
      <p className="text-xs leading-relaxed text-ink-muted">
        اگر در محیط توسعه هستید، مطمئن شوید سرویس backend با دستور{' '}
        <code>uv run uvicorn qatran_agent.api.main:app --reload --port 8000</code> در حال
        اجراست.
      </p>
    </div>
  </div>
)

export const App = () => {
  const { data: meta, isPending, error } = useMetaQuery()

  if (isPending) {
    return <BootScreen />
  }
  if (error !== null || meta === undefined) {
    return <BootError message={error?.message ?? 'پیکربندی اولیه دریافت نشد.'} />
  }
  // نشانی مشتری که به دستیار زنده‌ای وصل نیست: صفحه‌ی روشن، نه خطای خام و نه
  // فهرست دستیارها — وجود بقیه‌ی نسخه‌ها به این بازدیدکننده مربوط نیست.
  if (meta.live_missing) {
    return <UnknownAssistant />
  }

  return (
    <MetaProvider meta={meta}>
      <Shell isStudio={meta.is_studio} />
    </MetaProvider>
  )
}

const Shell = ({ isStudio }: { isStudio: boolean }) => (
  <Routes>
    <Route element={<AppShell />}>
      {/* ---------------- مسیرهای مشترک ---------------- */}
      <Route index element={<Navigate to={isStudio ? '/studio' : '/consult'} replace />} />
      {/*
        نسخه‌ی مشتری تجربه‌ی گفت‌وگو می‌گیرد و کارگاه نمای تحلیلی مرحله‌ای.
        هر دو روی یک خط لوله و یک مجموعه روت سرور می‌نشینند؛ تفاوت فقط در شکل
        ارائه است، چون مخاطب متفاوت است: مشتری پاسخ می‌خواهد، کارگاه جزئیات.
      */}
      {/*
        انتخاب مود روی همان مسیر انجام می‌شود و با query param نگه داشته می‌شود،
        پس `/consult` معنای قبلی‌اش را حفظ می‌کند و لینک‌های ذخیره‌شده نمی‌شکنند.
      */}
      <Route path="consult" element={<ConsultationHubPage isStudio={isStudio} />} />
      {/*
        آرشیو دو نسخه دارد. در کارگاه باید سازمان و دستیار انتخاب شوند، وگرنه
        سرور به لاگ سراسری برمی‌گردد و ادمین پرسش‌های مشتری را نمی‌بیند. نسخه‌ی
        مشتری scope ندارد چون تک‌دستیار است و سرور خودش از slug تشخیص می‌دهد.
      */}
      <Route
        path="archive"
        element={isStudio ? <Lazy element={<StudioArchivePage />} /> : <ArchivePage />}
      />

      {/* ---------------- مسیرهای استودیو ---------------- */}
      {/*
        مسیرها منبع‌محورند: فهرست، ساخت، و صفحه‌ی خودِ منبع. ترتیب ثبت مهم است —
        `new` و `bulk` باید پیش از الگوی پارامتری بیایند، وگرنه به‌عنوان شناسه
        خوانده می‌شوند.
      */}
      {isStudio && (
        <>
          <Route path="studio" element={<Navigate to="/studio/organizations" replace />} />
          {/* مسیر قدیمی مفرد، برای لینک‌های ذخیره‌شده */}
          <Route
            path="studio/organization"
            element={<Navigate to="/studio/organizations" replace />}
          />

          <Route
            path="studio/organizations"
            element={<Lazy element={<OrganizationsListPage />} />}
          />
          <Route
            path="studio/organizations/new"
            element={<Lazy element={<OrganizationDetailPage />} />}
          />
          <Route
            path="studio/organizations/:organizationId"
            element={<Lazy element={<OrganizationDetailPage />} />}
          />

          <Route path="studio/assistants" element={<Lazy element={<AssistantsListPage />} />} />
          <Route
            path="studio/assistants/new"
            element={<Lazy element={<AssistantDetailPage />} />}
          />
          <Route
            path="studio/assistants/:assistantId"
            element={<Lazy element={<AssistantDetailPage />} />}
          />

          <Route path="studio/dossiers" element={<Lazy element={<DossiersListPage />} />} />
          <Route path="studio/dossiers/new" element={<Lazy element={<DossierWizardPage />} />} />
          <Route path="studio/dossiers/bulk" element={<Lazy element={<BulkUploadPage />} />} />
          {/* پیش از الگوی پارامتری، وگرنه `principles` شناسه‌ی پرونده خوانده می‌شود. */}
          <Route
            path="studio/dossiers/principles"
            element={<Lazy element={<PrinciplesReviewPage />} />}
          />
          <Route
            path="studio/principles"
            element={<Navigate to="/studio/dossiers/principles" replace />}
          />
          <Route
            path="studio/dossiers/:dossierId"
            element={<Lazy element={<DossierWizardPage />} />}
          />

          <Route path="studio/processing" element={<Lazy element={<ProcessingPage />} />} />
          <Route path="studio/playground" element={<Lazy element={<PlaygroundPage />} />} />
          <Route path="studio/publish" element={<Lazy element={<PublishPage />} />} />
          <Route path="studio/settings" element={<Lazy element={<SettingsPage />} />} />
        </>
      )}

      {/* مسیر ناشناخته به خانه برمی‌گردد، نه صفحه‌ی ۴۰۴.
          در نسخه‌ی مشتری، صفحه‌ی ۴۰۴ که مسیر استودیو را نام ببرد، وجود آن
          مسیرها را لو می‌داد. */}
      <Route path="*" element={<Navigate to={isStudio ? '/studio' : '/consult'} replace />} />
    </Route>
  </Routes>
)

/** پوشش `Suspense` برای صفحات تنبل. نشانگر بارگذاری کوچک و درجا. */
const Lazy = ({ element }: { element: ReactNode }) => (
  <Suspense
    fallback={
      <div
        className="flex items-center justify-center gap-2.5 py-24 text-sm text-ink-muted"
        role="status"
      >
        <Loader2 className="size-4 animate-spin text-accent" aria-hidden />
        در حال بارگذاری صفحه…
      </div>
    }
  >
    {element}
  </Suspense>
)
